import { forwardRef } from 'react';
import { motion } from 'motion/react';
import { cn } from './cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  /** Icon rendered before the label. */
  startIcon?: React.ReactNode;
  /** Icon rendered after the label. */
  endIcon?: React.ReactNode;
  /** Stretch to fill the container width. */
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 font-semibold rounded-xl ' +
  'transition-all duration-200 active:scale-[0.98] select-none ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-canvas disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap';

const variants: Record<ButtonVariant, string> = {
  primary:
    'text-white bg-gradient-to-br from-primary to-primary-light ' +
    'hover:from-primary-dark hover:to-primary shadow-btn-primary dark:shadow-btn-primary-glow hover:-translate-y-px',
  secondary:
    'bg-surface-2 text-content border border-line hover:border-primary/40 hover:bg-primary/5',
  outline:
    'bg-transparent text-primary border-2 border-primary/60 hover:border-primary hover:bg-primary/5',
  ghost: 'bg-transparent text-content hover:bg-primary/10 hover:text-primary',
  danger: 'text-white bg-danger hover:bg-danger/90 shadow-sm',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-sm',
  md: 'h-11 px-5 text-sm',
  lg: 'h-12 px-7 text-base',
};

/**
 * Primary action button — replaces MUI <Button />.
 * variant: primary | secondary | outline | ghost | danger (danger = destructive only).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      startIcon,
      endIcon,
      fullWidth,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => (
    <motion.button
      ref={ref}
      disabled={disabled || loading}
      whileTap={disabled || loading ? undefined : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...(props as React.ComponentPropsWithoutRef<typeof motion.button>)}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 20 : 18} className="text-current" />
      ) : (
        startIcon
      )}
      {children}
      {!loading && endIcon}
    </motion.button>
  ),
);

Button.displayName = 'Button';
